import { omit } from 'lodash'
import BaseApi from '@/api/Base/BaseApi'
import {
  CreateExternalModelPayload,
  UpdateExternalModelPayload,
  GetExternalModelListResponse,
  GetExternalModelListPayload,
  GetTrainableExternalModelListPayload,
  GetHuggingFaceModelListResponse,
  ImportHuggingFaceModelPayload,
  HuggingFaceModelFilterType,
  HuggingFaceModel,
  CreateExternalModelNodePayload,
  ExternalModelNodeInfo,
  RegisterTrainableExternalModelPayload
} from '@/api/ExternalModel/types'

import { ModelValueType } from '@/pages/const'

const ROOT = '/external-model'

export default class ExternalModelApi extends BaseApi {
  createExternalModel(payload: CreateExternalModelPayload) {
    const { modelName, modelType, description, zipFile, workspaceId, inferenceModel = true } = payload

    const formData = new FormData()

    formData.append('modelType', modelType)
    formData.append('modelName', modelName)
    formData.append('workspaceId', workspaceId)
    formData.append('zipFile', zipFile)
    formData.append('inferenceModel', inferenceModel.toString())

    if (description) {
      formData.append('description', description)
    }

    return this.agent.post(`${ROOT}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  deleteExternalModel(externalModelId: string) {
    return this.agent.delete(`${ROOT}/${externalModelId}`)
  }

  updateExternalModel(payload: UpdateExternalModelPayload) {
    const { externalModelId, ...rest } = payload

    return this.agent.put(`${ROOT}/${externalModelId}`, omit(rest, ['workspaceId']))
  }

  getExternalModelList(workspaceId: string, params: GetExternalModelListPayload) {
    const { page, itemsPerPage = 3, modelCapability } = params

    return this.agent.get<GetExternalModelListResponse>(`${ROOT}/${workspaceId}`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        modelCapability,
      },
    })
  }

  getTrainableExternalModelList(workspaceId: string, params: GetTrainableExternalModelListPayload) {
    const {
      modelType,
      page,
      itemsPerPage = 25,
      sortKey = 'createdAt',
      sortDir = 'desc',
    } = params

    return this.agent.get<GetExternalModelListResponse>(`${ROOT}/${workspaceId}/trainable`, {
      params: {
        modelType,
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
      },
    })
  }

  createExternalModelNode(payload: CreateExternalModelNodePayload) {
    const { projectId, ...rest } = payload

    return this.agent.post<ExternalModelNodeInfo>(`/project/${projectId}/externalModelNode/create`, rest)
  }

  getExternalModelNodeDetail(externalModelNodeId: string) {
    return this.agent.get<ExternalModelNodeInfo>(`external-model-node/${externalModelNodeId}`)
  }

  getHuggingFaceModelList(workspaceId: string, params: { page: number, filterType?: HuggingFaceModelFilterType, itemsPerPage?: number }) {
    const { page, filterType = 'ALL', itemsPerPage = 10 } = params

    return this.agent.get<GetHuggingFaceModelListResponse>(`/huggingFaceModel/${workspaceId}`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        filterType,
      },
    })
  }

  getEnabledHuggingFaceModelList(workspaceId: string, modelType: ModelValueType) {
    return this.agent.get<HuggingFaceModel[]>(`/huggingFaceModel/${workspaceId}/list`, {
      params: {
        modelType,
      },
    })
  }

  importHuggingFaceModel(payload: ImportHuggingFaceModelPayload) {
    const { workspaceId, ...rest } = payload

    return this.agent.put(`/huggingFaceModel/${workspaceId}`, rest)
  }

  registerTrainableExternalModel(payload: RegisterTrainableExternalModelPayload) {
    const {
      workspaceId,
      modelType,
      modelName,
      externalFileType,
      externalModelInputFormat,
      description,
      modelFile,
      parametersFile,
      manifestFile,
      modelFiles,
      lossFiles,
      scheduleFiles,
    } = payload

    const formData = new FormData()

    formData.append('workspaceId', workspaceId)
    formData.append('modelType', modelType)
    formData.append('modelName', modelName)
    formData.append('externalFileType', externalFileType)
    formData.append('externalModelInputFormat', externalModelInputFormat)

    if (description) {
      formData.append('description', description)
    }

    if (modelFile) {
      formData.append('modelFile', modelFile)
    }

    if (parametersFile) {
      formData.append('parametersFile', parametersFile)
    }

    if (manifestFile) {
      formData.append('manifestFile', manifestFile)
    }

    modelFiles?.forEach((file) => formData.append('modelFiles', file))
    lossFiles?.forEach((file) => formData.append('lossFiles', file))
    scheduleFiles?.forEach((file) => formData.append('scheduleFiles', file))

    return this.agent.post(`${ROOT}/trainable`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }
}
